/**
 * FULL-SCREEN DRAWING SHEET OVERLAY // KEVIN ARTHUR DE LIMA
 * Detailed engineering drawing overlay with technical title block and photo/schematic view.
 */

export function initProjectOverlay(projects, lenis) {
  const overlay = document.getElementById('project-drawing-overlay');
  if (!overlay) return null;

  overlay.setAttribute('data-lenis-prevent', 'true');
  overlay.addEventListener('wheel', e => {
    e.stopPropagation();
  }, { passive: true });

  let activeProject = null;
  let lastFocusedEl = null;

  function open(slug) {
    const proj = projects.find(p => p.slug === slug);
    if (!proj) return;
    activeProject = proj;
    lastFocusedEl = document.activeElement;

    if (lenis) lenis.stop();

    overlay.innerHTML = `
      <div class="dwg-sheet-inner">
        <button class="dwg-overlay-close-btn" id="dwg-close-btn">[ ESC / CLOSE DWG ]</button>
        
        <div class="dwg-header-block" style="margin-bottom: 24px;">
          <div>
            <div class="dwg-number">${proj.dwg} // ${proj.projectNo} // ${proj.rev}</div>
            <h2 class="dwg-title" style="font-size: 2rem;">${proj.title}</h2>
          </div>
          <div class="dwg-meta-pill">
            ${proj.status}<br>
            ROLE: ${proj.role}
          </div>
        </div>

        <div class="dwg-overlay-img-slot">
          ${proj.videoUrl ? `
            <video src="${proj.videoUrl}" controls playsinline muted loop poster="${proj.image}" style="width: 100%; max-height: 480px; object-fit: contain; background: #000000; border: 1px solid var(--paper-2);"></video>
          ` : `
            <img src="${proj.image}" alt="${proj.title}" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
            <div class="project-thumb-placeholder" style="display: none; height: 380px;">
              TECHNICAL SCHEMATIC FILE MISSING<br>
              [ DROP PHOTO AT ${proj.image} ]
            </div>
          `}
        </div>

        <div style="display: grid; grid-template-columns: repeat(12, 1fr); gap: 24px; margin-bottom: 32px;">
          <div style="grid-column: 1 / 8;">
            <p style="font-weight: 600; font-size: 1.125rem; color: #0B0B0B; margin-bottom: 16px;">
              ${proj.heroLine}
            </p>
            <ul class="project-bullets">
              ${proj.bullets.map(b => `<li>${b}</li>`).join('')}
            </ul>
            ${proj.paperUrl ? `
              <div style="margin-top: 20px;">
                <a href="${proj.paperUrl}" download class="btn-solid-cv" style="font-size: 0.75rem; padding: 10px 18px; display: inline-block; text-decoration: none;">
                  [ DOWNLOAD CAPSTONE RESEARCH PAPER (.DOCX) ]
                </a>
              </div>
            ` : ''}
          </div>
          <div style="grid-column: 9 / 13;">
            <table class="title-block-table">
              <tr><th>MATERIAL</th><td>${proj.material}</td></tr>
              <tr><th>MASS</th><td>${proj.mass}</td></tr>
              <tr><th>POWER</th><td>${proj.power}</td></tr>
              <tr><th>DATE</th><td>${proj.date}</td></tr>
              <tr><th>STACK</th><td>${proj.stack.join(', ')}</td></tr>
            </table>
          </div>
        </div>
      </div>
    `;

    overlay.classList.add('active');
    overlay.scrollTop = 0;
    document.body.style.overflow = 'hidden';

    const vid = overlay.querySelector('video');
    if (vid) {
      vid.muted = true;
      vid.volume = 0;
    }

    const closeBtn = document.getElementById('dwg-close-btn');
    if (closeBtn) {
      closeBtn.focus();
      closeBtn.addEventListener('click', close);
    }
  }

  function openDocument(doc) {
    lastFocusedEl = document.activeElement;
    if (lenis) lenis.stop();
    overlay.innerHTML = `
      <div class="dwg-sheet-inner">
        <button class="dwg-overlay-close-btn" id="dwg-close-btn">[ ESC / CLOSE DWG ]</button>
        
        <div class="dwg-header-block" style="margin-bottom: 24px;">
          <div>
            <div class="dwg-number">${doc.dwg || 'DOC-01'} // ACADEMIC ENDORSEMENT</div>
            <h2 class="dwg-title" style="font-size: 2rem;">${doc.title}</h2>
          </div>
          <div class="dwg-meta-pill">
            ISSUED BY: ${doc.author}<br>
            ${doc.role}
          </div>
        </div>

        <div class="dwg-overlay-img-slot" style="max-height: 720px; background: #FFFFFF;">
          <img src="${doc.image}" alt="${doc.title}" style="max-height: 720px; object-fit: contain; filter: grayscale(1) contrast(1.15);">
        </div>

        <div style="border-top: 1px solid var(--sheet-line); padding-top: 20px; margin-top: 20px;">
          <p style="font-family: var(--font-mono); font-size: 0.8125rem; color: #0B0B0B; line-height: 1.6; margin-bottom: 12px;">
            <strong>SUPERVISOR EXCERPT:</strong> "${doc.quote}"
          </p>
          <div style="display: flex; gap: 16px; align-items: center; margin-top: 16px;">
            <a href="${doc.image}" target="_blank" rel="noopener noreferrer" class="btn-solid-cv" style="font-size: 0.75rem; padding: 8px 16px;">
              [ OPEN FULL RESOLUTION SCAN ]
            </a>
            <span style="font-family: var(--font-mono); font-size: 0.6875rem; color: var(--sheet-mute); font-weight: 600;">
              FACULTY OF ENGINEERING &amp; TECHNOLOGY // SUNWAY UNIVERSITY
            </span>
          </div>
        </div>
      </div>
    `;

    overlay.classList.add('active');
    overlay.scrollTop = 0;
    document.body.style.overflow = 'hidden';

    const closeBtn = document.getElementById('dwg-close-btn');
    if (closeBtn) {
      closeBtn.focus();
      closeBtn.addEventListener('click', close);
    }
  }

  function close() {
    const vid = overlay.querySelector('video');
    if (vid) {
      vid.pause();
    }
    overlay.classList.remove('active');
    document.body.style.overflow = '';
    if (lenis) lenis.start();
    if (lastFocusedEl) lastFocusedEl.focus();
    activeProject = null;
  }

  window.addEventListener('keydown', e => {
    if (e.key === 'Escape' && overlay.classList.contains('active')) {
      close();
    }
  });

  return { open, openDocument, close };
}
